/**
 * ========================
 * RESOURCES DOMAIN MENU PROVIDER
 * ========================
 *
 * Sidebar menu configuration for the Resource Centre domain.
 *
 * Sections:
 *  1. Overview      — Dashboard
 *  2. Catalogue     — Courses, Sessions, Prerequisites
 *  3. Knowledge     — Knowledge Base
 *  4. Insights      — Learner Progress, Analytics
 *  5. Administration — Lookups (Resources Access role)
 *
 * Gating: every entry requires the "Resources Access" role.
 * Admin override is bypassed via strictAccess where the role is the only
 * legitimate way to enter — this matches Tasks / Vendors / Members.
 */

import {
  IconLayoutDashboard,
  IconBooks,
  IconCategory2,
  IconBookmark,
  IconChartBar,
  IconUsersGroup,
  IconSettings,
} from '@tabler/icons-react';
import { DomainMenuProvider, MenuSection } from '../../layouts/menu/types';
import { anyVisibilityCheck } from './BaseMenuProvider';
import { useResourceBadgesStore } from '@/domain/badges/resourceBadgesStore';

class ResourcesMenuProvider implements DomainMenuProvider {
  domainId = 'resources';

  getMenuConfig() {
    // Anyone with the "Resources Access" role OR Resource.Read permission can
    // see the read-only catalogue + knowledge surface.
    const allUsers = anyVisibilityCheck(
      { roles: ['Resources Access'] },
      { permissions: ['Resource.Read'] }
    );

    // Admins (Resources Access role OR Resource.Admin / Admin role) get the
    // lookup editor.
    const admins = anyVisibilityCheck(
      { roles: ['Resources Access', 'Admin'] },
      { permissions: ['Resource.Admin'] }
    );

    const sections: MenuSection[] = [
      // 1. Overview
      {
        id: 'resources-overview',
        label: 'Overview',
        items: [
          {
            id: 'resources-dashboard',
            label: 'Dashboard',
            path: '/app/resources',
            icon: <IconLayoutDashboard size={16} />,
            visible: allUsers,
          },
        ],
      },

      // 2. Catalogue
      {
        id: 'resources-catalogue',
        label: 'Catalogue',
        items: [
          {
            id: 'resources-courses',
            label: 'Courses',
            path: '/app/resources/courses',
            icon: <IconBooks size={16} />,
            visible: allUsers,
            countSelector: () => useResourceBadgesStore.getState().coursesInDraft || null,
            badgeColor: 'yellow',
          },
          {
            id: 'resources-categories',
            label: 'Categories',
            path: '/app/resources/categories',
            icon: <IconCategory2 size={16} />,
            visible: allUsers,
          },
        ],
      },

      // 3. Knowledge
      {
        id: 'resources-knowledge',
        label: 'Knowledge',
        items: [
          {
            id: 'resources-kb',
            label: 'Knowledge Base',
            path: '/app/resources/knowledge-base',
            icon: <IconBookmark size={16} />,
            visible: allUsers,
            countSelector: () => useResourceBadgesStore.getState().kbInDraft || null,
            badgeColor: 'yellow',
          },
        ],
      },

      // 4. Insights
      {
        id: 'resources-insights',
        label: 'Insights',
        items: [
          {
            id: 'resources-progress',
            label: 'Learner Progress',
            path: '/app/resources/progress',
            icon: <IconUsersGroup size={16} />,
            visible: allUsers,
          },
          {
            id: 'resources-analytics',
            label: 'Analytics',
            path: '/app/resources/analytics',
            icon: <IconChartBar size={16} />,
            visible: allUsers,
          },
        ],
      },

      // 5. Administration
      {
        id: 'resources-admin',
        label: 'Administration',
        visible: admins,
        items: [
          {
            id: 'resources-lookups',
            label: 'Lookups',
            path: '/app/resources/admin/lookups',
            icon: <IconSettings size={16} />,
            visible: admins,
          },
          {
            id: 'resources-settings',
            label: 'Settings',
            path: '/app/resources/admin/settings',
            icon: <IconSettings size={16} />,
            visible: admins,
          },
        ],
      },
    ];

    return {
      domainId: this.domainId,
      sections,
      metadata: {
        version: '2.0.0',
        lastUpdated: Date.now(),
        description: 'Resource Centre menu — sectioned layout with Resources Access gating',
      },
    };
  }
}

export const resourcesMenuProvider = new ResourcesMenuProvider();
